/**
 * Phone Numbers Page
 *
 * Displays the list of phone numbers with filtering, sorting, and pagination.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Logger, LogCategory } from '@/utils/logger';
import { AppLayout, PageHeader } from '@/components/layout';
import { Button } from '@/components/ui/Button';
import { Pagination } from '@/components/ui/Pagination';
import {
  PhoneTable,
  PhoneFilters,
  BulkActions,
  CsvUploadModal,
  AddPhoneModal,
} from '@/components/phone';
import { VerifyNumberModal } from '@/components/phone/VerifyNumberModal';
import { usePhoneStore, usePhonePagination } from '@/stores/phoneStore';
import { Plus, Upload, Download, ShieldCheck } from 'lucide-react';

export function PhoneNumbersPage() {
  const [searchParams] = useSearchParams();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);

  const {
    fetchPhones,
    fetchCallGroups,
    setFilters,
    setPage,
    setPageSize,
    getFilteredPhones,
  } = usePhoneStore();

  const pagination = usePhonePagination();

  // Initialize filters from URL params
  useEffect(() => {
    Logger.info(LogCategory.UI, 'PhoneNumbersPage mounted', 'PhoneNumbersPage');
    Logger.startTimer('phoneNumbersPageLoad');

    // Check for status filter in URL
    const statusParam = searchParams.get('status');
    if (statusParam === 'flagged' || statusParam === 'clean') {
      Logger.debug(LogCategory.DATA, 'Setting status filter from URL', 'PhoneNumbersPage', {
        status: statusParam,
      });
      setFilters({ status: statusParam });
    }

    // Fetch phones and call groups
    fetchPhones();
    fetchCallGroups();

    return () => {
      Logger.endTimer('phoneNumbersPageLoad');
    };
  }, [fetchPhones, fetchCallGroups, searchParams, setFilters]);

  const handleExportCsv = () => {
    Logger.info(LogCategory.DATA, 'Exporting phone numbers to CSV', 'PhoneNumbersPage');

    const phones = getFilteredPhones();
    const headers = ['Phone Number', 'Status', 'Reputation Score', 'Group', 'Last Flagged', 'Sources'];
    const rows = phones.map((phone) => [
      phone.formattedNumber,
      phone.flagStatus,
      phone.reputationScore,
      phone.callGroup || '',
      phone.lastFlaggedDate || '',
      phone.reputationSources.join('; '),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `phone_numbers_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    Logger.info(LogCategory.DATA, 'CSV export complete', 'PhoneNumbersPage', {
      rowCount: phones.length,
    });
  };

  return (
    <AppLayout>
      <PageHeader
        title="Phone Numbers"
        description="Monitor and manage your phone number reputation"
        actions={
          <>
            <Button
              variant="outline"
              leftIcon={<ShieldCheck className="h-4 w-4" />}
              onClick={() => setShowVerifyModal(true)}
            >
              Verify Numbers
            </Button>
            <Button
              variant="outline"
              leftIcon={<Download className="h-4 w-4" />}
              onClick={handleExportCsv}
            >
              Export CSV
            </Button>
            <Button
              variant="outline"
              leftIcon={<Upload className="h-4 w-4" />}
              onClick={() => {
                Logger.info(LogCategory.UI, 'Opening CSV upload modal', 'PhoneNumbersPage');
                setShowUploadModal(true);
              }}
            >
              Upload CSV
            </Button>
            <Button
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => {
                Logger.info(LogCategory.UI, 'Opening add phone modal', 'PhoneNumbersPage');
                setShowAddModal(true);
              }}
            >
              Add Number
            </Button>
          </>
        }
      />

      {/* Bulk Actions */}
      <BulkActions />

      {/* Filters */}
      <PhoneFilters />

      {/* Table */}
      <PhoneTable />

      {/* Pagination */}
      <div className="mt-4">
        <Pagination
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>

      {/* CSV Upload Modal */}
      <CsvUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
      />

      {/* Add Phone Modal */}
      <AddPhoneModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
      />

      {/* Verify Number Modal */}
      <VerifyNumberModal
        isOpen={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
      />
    </AppLayout>
  );
}
